import { useState, useEffect, useCallback } from 'react';
import { useAuth, useTranslation } from '@/context/AuthContext';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { getFavorites, removeFavorite } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Heart, MapPin, Star, Navigation, Trash2, Loader2, 
  Headphones, ExternalLink, Clock, Search, ChevronRight, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Favorites() {
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const { startAudioGuide, isGenerating } = useAudioPlayer();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [versionPickerFavorite, setVersionPickerFavorite] = useState(null);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await getFavorites();
      setFavorites(response.data?.favorites || []);
    } catch (err) {
      console.error('Error loading favorites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (placeId, name) => {
    if (!window.confirm(language === 'fr' 
      ? `Retirer "${name}" de vos favoris ?` 
      : `Remove "${name}" from favorites?`)) {
      return;
    }
    
    setRemoving(placeId);
    try {
      await removeFavorite(placeId);
      setFavorites(prev => prev.filter(f => f.place_id !== placeId));
    } catch (err) {
      console.error('Error removing favorite:', err);
    } finally {
      setRemoving(null);
    }
  };

  const handleAudioGuide = useCallback((favorite) => {
    setVersionPickerFavorite({
      name: favorite.name,
      category: favorite.category || '',
      description: favorite.description || '',
      address: favorite.address || '',
      lat: favorite.lat,
      lng: favorite.lng,
    });
  }, []);

  const handleVersionSelect = useCallback(async (version) => {
    if (!versionPickerFavorite) return;
    const place = versionPickerFavorite;
    setVersionPickerFavorite(null);
    await startAudioGuide(place, { version, lang: language });
  }, [versionPickerFavorite, language, startAudioGuide]);

  const filteredFavorites = favorites.filter(f => 
    !searchQuery || 
    f.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500 fill-pink-500" />
            {language === 'fr' ? 'Mes Favoris' : 'My Favorites'}
          </h1>
          <p className="text-gray-500 mt-1">
            {language === 'fr' 
              ? `${favorites.length} lieu${favorites.length > 1 ? 'x' : ''} sauvegardé${favorites.length > 1 ? 's' : ''}`
              : `${favorites.length} saved place${favorites.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        
        {/* Search */}
        {favorites.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
              className="pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none text-sm w-full sm:w-64"
              data-testid="favorites-search"
            />
          </div>
        )}
      </div>

      {/* Empty state */}
      {favorites.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-pink-100 flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-pink-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {language === 'fr' ? 'Aucun favori' : 'No favorites yet'}
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              {language === 'fr' 
                ? 'Explorez les lieux avec Instant Pulse et ajoutez vos coups de coeur ici !'
                : 'Explore places with Instant Pulse and save your favorites here!'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Favorites grid */}
      {filteredFavorites.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {filteredFavorites.map((favorite, index) => (
              <motion.div
                key={favorite.favorite_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow group">
                  {/* Photo */}
                  {favorite.photo ? (
                    <div className="h-36 bg-gradient-to-br from-purple-100 to-pink-100 overflow-hidden">
                      <img 
                        src={favorite.photo} 
                        alt={favorite.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="h-36 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                      <MapPin className="w-12 h-12 text-purple-300" />
                    </div>
                  )}
                  
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 truncate">{favorite.name}</h3>
                        {favorite.category && (
                          <span className="inline-block px-2 py-0.5 mt-1 rounded-md bg-purple-100 text-purple-700 text-[10px] font-medium uppercase">
                            {favorite.category}
                          </span>
                        )}
                      </div>
                      
                      {favorite.rating && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-yellow-50">
                          <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs font-semibold text-yellow-700">{favorite.rating}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Address */}
                    {favorite.address && (
                      <p className="text-xs text-gray-500 mb-2 truncate flex items-center gap-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        {favorite.address}
                      </p>
                    )}
                    
                    {/* Description */}
                    {favorite.description && (
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                        {favorite.description}
                      </p>
                    )}
                    
                    {/* Source & Date */}
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-3">
                      <span className="px-1.5 py-0.5 bg-gray-100 rounded">
                        {favorite.source === 'instant_pulse' ? 'Instant Pulse' : favorite.source}
                      </span>
                      <Clock className="w-3 h-3" />
                      {new Date(favorite.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          const mapsUrl = favorite.lat && favorite.lng 
                            ? `https://www.google.com/maps/dir/?api=1&destination=${favorite.lat},${favorite.lng}`
                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(favorite.name)}`;
                          window.open(mapsUrl, '_blank');
                        }}
                      >
                        <Navigation className="w-3.5 h-3.5 mr-1.5" />
                        {language === 'fr' ? 'Y aller' : 'Go'}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-purple-200 hover:bg-purple-50"
                        onClick={() => handleAudioGuide(favorite)}
                        data-testid={`fav-audio-guide-btn-${favorite.place_id}`}
                      >
                        <Headphones className="w-3.5 h-3.5" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRemove(favorite.place_id, favorite.name)}
                        disabled={removing === favorite.place_id}
                      >
                        {removing === favorite.place_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* No results for search */}
      {favorites.length > 0 && filteredFavorites.length === 0 && searchQuery && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {language === 'fr' 
              ? `Aucun résultat pour "${searchQuery}"`
              : `No results for "${searchQuery}"`}
          </p>
        </div>
      )}

      {/* Version Picker Modal */}
      <AnimatePresence>
        {versionPickerFavorite && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="font-bold text-gray-900">Audio Guide</h3>
                  <p className="text-xs text-gray-500 truncate max-w-[220px]">{versionPickerFavorite.name}</p>
                </div>
                <button
                  onClick={() => setVersionPickerFavorite(null)}
                  className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-gray-600 text-center mb-2">
                  {language === 'fr' ? 'Choisissez la durée de votre visite :' : 'Choose your tour duration:'}
                </p>
                {[
                  { id: 'short', label: language === 'fr' ? 'Courte' : 'Short', minutes: 2, description: language === 'fr' ? "L'essentiel en 2 min" : 'Essentials in 2 min', chapters: 3, color: 'from-green-400 to-green-600', icon: '⚡' },
                  { id: 'medium', label: language === 'fr' ? 'Moyenne' : 'Medium', minutes: 5, description: language === 'fr' ? 'Visite complète en 5 min' : 'Complete tour in 5 min', chapters: 5, color: 'from-purple-500 to-violet-600', icon: '🎧' },
                  { id: 'long', label: language === 'fr' ? 'Longue' : 'Long', minutes: 10, description: language === 'fr' ? 'Immersion totale en 10 min' : 'Full immersion in 10 min', chapters: 7, color: 'from-rose-500 to-pink-600', icon: '🎭' },
                ].map(v => (
                  <button
                    key={v.id}
                    onClick={() => handleVersionSelect(v.id)}
                    disabled={isGenerating}
                    data-testid={`fav-version-${v.id}`}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-purple-50 border-2 border-gray-100 hover:border-purple-300 hover:shadow-md transition-all group active:scale-[.98] disabled:opacity-50"
                  >
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br ${v.color} shadow-lg flex-shrink-0`}>
                      {v.icon}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{v.label}</span>
                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                          ~{v.minutes} min
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{v.description}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {v.chapters} {language === 'fr' ? 'chapitres' : 'chapters'}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
